import React, { useCallback, useMemo, useState } from 'react'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { connect } from 'react-redux'
import { List, Map, fromJS } from 'immutable'
import _get from 'lodash/get'
import _omit from 'lodash/omit'
import { useQuery, useMutation } from '@apollo/client'
import { getPrimary } from '../../services/languages/index'
import { getBoundActions } from '../../actions/index'
import { GET_USER_HISTORY } from '../../services/community/query'
import { FLAG_WATCHED_VIDEO } from '../../services/playlist/query'
import {
  APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from '../../services/apollo-graphql/index'
import RowV3 from '../Row.v3/index'
import { STYLES } from '../Row.v3/utils'
import TileV2Graphql from '../Tile.v2/Tile.v2Graphql/index'
import LoadMore from '../LoadMore/index'
import Button, { TYPES } from '../Button/index'
import IconV2, { ICON_TYPES, ICON_STYLES } from '../Icon.v2/index'
import { H1, H2, HEADING_TYPES } from '../Heading/index'
import {
  PLAYLIST_FETCH_LIMIT,
  getPlaceholderList,
} from './utils'

function flattenReferences (refs) {
  if (!refs || !refs.length) return []
  return refs.reduce((acc, ref) => {
    const type = _get(ref, 'contentReferenceType.type', '')
    const targets = ref.contentTo || []
    targets.forEach((to) => {
      const cId = to.contentId != null ? String(to.contentId) : ''
      acc.push({
        type,
        reference_type: type,
        name: to.title || '',
        title: to.title || '',
        path: to.path || '',
        contentId: cId,
        content_id: cId,
        language: to.language || '',
      })
    })
    return acc
  }, [])
}

function buildTileFromEntry (entry) {
  const featured = _get(entry, 'media.contentFeatured[0]', null)
  if (!featured) return null

  const assetUrl = _get(featured, 'contentAssetReferences[0].asset.renderUrl', '')
  const legacyUrl = _get(featured, 'imageContentRelationships[0].image.url', '')

  const imageRelationships = featured.imageContentRelationships || []
  const images = imageRelationships.map((rel) => ({
    type: rel.type || '',
    url: _get(rel, 'image.url', ''),
  }))
  if (assetUrl && !images.some((img) => img.type === 'd6-keyart-no-text')) {
    images.push({ type: 'd6-keyart-no-text', url: assetUrl })
  }
  if (!images.some((img) => img.type === 'd6-keyart-with-text') && (assetUrl || legacyUrl)) {
    images.push({ type: 'd6-keyart-with-text', url: assetUrl || legacyUrl })
  }

  const series = _get(featured, 'contentSeries[0]', null)
  const mediaDuration = _get(entry, 'media.duration', 0)
  const userPosition = entry.position ?? 0

  return {
    id: featured.contentId || entry.mediaId,
    nid: featured.contentId || entry.mediaId,
    contentId: featured.contentId || entry.mediaId,
    contentType: featured.contentType || 'video',
    title: featured.title || '',
    path: featured.path || '',
    shortDescription: featured.shortDescription || '',
    episodeNumber: featured.episodeNumber,
    seasonNumber: featured.seasonNumber,
    instructor: featured.instructor,
    isNew: featured.isNew,
    displayType: featured.displayType,
    subType: featured.subType,
    studio: featured.studio,
    duration: mediaDuration,
    featureMedia: {
      duration: mediaDuration,
      mediaId: _get(entry, 'media.mediaId', entry.mediaId),
      userMediaPosition: {
        position: userPosition,
      },
    },
    images,
    references: flattenReferences(featured.references || []),
    seriesTitle: series ? series.title : undefined,
    seriesPath: series ? series.path : undefined,
    seriesId: series ? series.contentId : undefined,
    hero: assetUrl || legacyUrl || '',
    heroAlt: featured.title || '',
    poster: legacyUrl || assetUrl || '',
    vote: _get(featured, 'contentAnalytic.votesUpCountTotal', 0),
    voteDown: (
      _get(featured, 'contentAnalytic.votesCountTotal', 0)
      - _get(featured, 'contentAnalytic.votesUpCountTotal', 0)
    ),
  }
}

const MyLibraryWatchHistory = (props) => {
  const {
    auth,
    user,
    staticText,
    addToasty,
  } = props

  const authJwt = auth.get('jwt')
  const language = getPrimary(user.getIn(['data', 'language'], List()))
  const placeholderList = getPlaceholderList()

  const [editMode, setEditMode] = useState(false)
  const [selectedItems, setSelectedItems] = useState({})

  const {
    data, loading, fetchMore, refetch,
  } = useQuery(GET_USER_HISTORY, {
    variables: {
      limit: PLAYLIST_FETCH_LIMIT,
      offset: 0,
      contentLanguage: [language, '_primary'],
    },
    skip: !authJwt,
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: { jwt: authJwt },
  })

  const [flagWatchedVideo, { loading: flagging }] = useMutation(FLAG_WATCHED_VIDEO, {
    context: { jwt: authJwt },
  })

  const watchHistory = _get(data, ['userMediaPositionList', 'userMediaPositions'], []) || []

  const tiles = useMemo(() => {
    const built = []
    watchHistory.forEach((entry) => {
      const tileData = buildTileFromEntry(entry)
      if (tileData) {
        built.push(fromJS(tileData))
      }
    })
    return List(built)
  }, [watchHistory])

  const displayItems = tiles.size && !loading ? tiles : placeholderList
  const hasItems = tiles.size > 0

  const handleEditSubmit = async () => {
    const itemsToHide = Object.values(selectedItems)
    if (itemsToHide.length && !flagging) {
      const promises = itemsToHide.map((entry) => {
        const videoId = _get(entry, 'media.contentFeatured[0].contentId', null)
        if (!videoId) return Promise.resolve()
        return flagWatchedVideo({
          variables: {
            videoId,
            hiddenByUser: true,
          },
        })
      })
      await Promise.all(promises)
      addToasty(staticText.get('savePlaylistSuccess'))
      setSelectedItems({})
      refetch()
    }
  }

  const handleEditClick = async () => {
    if (!editMode) {
      setEditMode(true)
    } else {
      setEditMode(false)
      handleEditSubmit()
    }
  }

  const toggleItemAddRemove = useCallback((entryIndex, isSelected) => {
    if (isSelected) {
      setSelectedItems((prevState) => _omit(prevState, [entryIndex]))
    } else {
      setSelectedItems((prevState) => ({
        ...prevState,
        [entryIndex]: watchHistory[entryIndex],
      }))
    }
  }, [watchHistory])

  const renderItem = useCallback((tile, index) => {
    const isSelected = !!selectedItems[index]

    if (editMode) {
      return (
        <div className="my-library__item-edit" key={tile.get('id') || index}>
          <div className={`my-library__item-edit-backdrop ${isSelected ? 'my-library__item-edit-backdrop--add' : ''}`}></div>
          <Button
            type={TYPES.LINK}
            onClick={() => toggleItemAddRemove(index, isSelected)}
            className={`my-library__item-edit-button my-library__item-edit-button--${isSelected ? 'add' : 'remove'}`}
          >
            <IconV2
              className="my-library__item-edit-icon"
              type={isSelected ? ICON_TYPES.PLUS : ICON_TYPES.CLOSE}
            />
          </Button>
          <TileV2Graphql
            data={tile}
            adminTitle="My Library"
            index={index}
            uiLocation="My Library"
          />
        </div>
      )
    }

    return (
      <TileV2Graphql
        key={tile.get('id') || index}
        data={tile}
        adminTitle="My Library"
        index={index}
        uiLocation="My Library"
      />
    )
  }, [editMode, selectedItems, toggleItemAddRemove])

  return (
    <div className="my-library__content">
      <header className="my-library__playlist-header">
        <H1>{staticText.get('watchHistoryTitle')}</H1>
        <div className="my-library__playlist-header-buttons">
          {hasItems
            && <Button type={TYPES.GHOST} onClick={handleEditClick}>
              <span className="my-library__playlist-header-button-text">
                {editMode ? staticText.get('saveButtonText') : staticText.get('editButtonText')}
              </span>
              <IconV2
                type={editMode ? ICON_TYPES.CIRCULAR_CHECK : ICON_TYPES.PENCIL}
                style={editMode ? ICON_STYLES.OUTLINE : ICON_STYLES.TEAL_CIRCLE}
              />
            </Button>
          }
        </div>
      </header>
      {hasItems || loading
        ? <>
          <RowV3
            asGrid
            style={STYLES.S12334}
          >
            {displayItems.map((item, index) => renderItem(item, index))}
          </RowV3>
          <LoadMore
            onLoadMore={() => {
              fetchMore({
                variables: {
                  offset: watchHistory.length,
                },
              })
            }}
            buttonClassName={['my-library__load-more-button', 'button--secondary']}
            currentCount={tiles.size}
            totalCount={_get(data, ['userMediaPositionList', 'count'], tiles.size)}
            processing={loading || !tiles.size}
            infiniteScroll
          />
        </>
        : <div className="my-library__content-empty">
          <div className="my-library__content-empty-image" />
          <div className="my-library__content-empty-text">
            <H2 as={HEADING_TYPES.H5}>{staticText.get('watchHistoryEmptyTitle')}</H2>
            <p className="my-library__content-empty-copy">
              {staticText.get('watchHistoryEmptyText1')}
            </p>
          </div>
        </div>
      }
    </div>
  )
}

MyLibraryWatchHistory.propTypes = {
  auth: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
}

export default connect(
  (state) => {
    return {
      auth: state.auth,
      user: state.user,
      staticText: state.staticText.getIn(['data', 'myLibraryPage', 'data'], Map()) || Map(),
    }
  },
  (dispatch) => {
    const actions = getBoundActions(dispatch)
    return {
      addToasty: actions.toasty.addToasty,
    }
  },
)(MyLibraryWatchHistory)
